import { apiConfig } from '@nomiss/configs';
import { ApiRequestError, executeSdkEnvelopeRequest } from './api-request.util';
import {
  adminContentApi,
  buildApiHeaders,
  fetchWithAuthRecovery,
} from './sdk-client';

export class AdminContentError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminContentError');
  }
}

export type UpdateHighlightUrlResult = {
  message: string;
};

export type AdminOttMapping = {
  id: string;
  sportId: string;
  sportName: string;
  leagueId: string | null;
  leagueName: string | null;
  ottPlatformId: string;
  ottPlatformName: string;
  priority: number;
  listingUrl: string | null;
};

export type CreateOttMappingInput = {
  sport_id: string;
  league_id: string;
  ott_platform_id: string;
  priority?: number;
};

export type UpdateOttMappingInput = {
  ott_platform_id?: string;
  priority?: number;
  listing_url?: string | null;
};

export type AdminYouTubeChannel = {
  id: string;
  code: string;
  name: string;
  channelId: string;
  channelUrl: string | null;
};

export type CreateYouTubeChannelInput = {
  code: string;
  name: string;
  channel_id: string;
  channel_url: string;
};

export type UpdateYouTubeChannelInput = {
  name?: string;
  channel_id?: string;
  channel_url?: string;
};

export type AdminLeagueChannelMapping = {
  id: string;
  leagueId: string;
  leagueName: string;
  youtubeChannelId: string;
  channelName: string;
  priority: number;
  requiresReview: boolean;
};

export type CreateLeagueChannelMappingInput = {
  league_id: string;
  youtube_channel_id: string;
  priority?: number;
};

export type AdminOttMappingOption = {
  id: string;
  code: string;
  name: string;
};

export type AdminOttMappingLeagueOption = AdminOttMappingOption & {
  sportId: string;
};

export type AdminOttMappingOptions = {
  sports: AdminOttMappingOption[];
  leagues: AdminOttMappingLeagueOption[];
  ottPlatforms: AdminOttMappingOption[];
};

export type AdminMutationMessageResult = {
  message: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseOttMapping(value: unknown): AdminOttMapping | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    sportId: asString(value['sportId'] ?? value['sport_id']),
    sportName: asString(value['sportName'] ?? value['sport_name']),
    leagueId: asNullableString(value['leagueId'] ?? value['league_id']),
    leagueName: asNullableString(value['leagueName'] ?? value['league_name']),
    ottPlatformId: asString(value['ottPlatformId'] ?? value['ott_platform_id']),
    ottPlatformName: asString(
      value['ottPlatformName'] ?? value['ott_platform_name'],
    ),
    priority: asNumber(value['priority'], 1),
    listingUrl: asNullableString(value['listingUrl'] ?? value['listing_url']),
  };
}

function parseYouTubeChannel(value: unknown): AdminYouTubeChannel | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    code: asString(value.code),
    name: asString(value.name),
    channelId: asString(value.channelId),
    channelUrl: asNullableString(value.channelUrl),
  };
}

function parseLeagueChannelMapping(
  value: unknown,
): AdminLeagueChannelMapping | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    leagueId: asString(value.leagueId),
    leagueName: asString(value.leagueName),
    youtubeChannelId: asString(value.youtubeChannelId),
    channelName: asString(value.channelName),
    priority: asNumber(value.priority, 1),
    requiresReview:
      value['requiresReview'] === true || value['requires_review'] === true,
  };
}

function parseMessageResult(
  json: unknown,
  fallback: string,
): AdminMutationMessageResult {
  if (isRecord(json) && typeof json.message === 'string') {
    return { message: json.message };
  }
  return { message: fallback };
}

export async function updateHighlightUrl(
  id: string,
  url: string,
  thumbnailUrl?: string,
): Promise<UpdateHighlightUrlResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerUpdateHighlightUrlV1Raw({
        id,
        highlightUrlDto: {
          url,
          ...(thumbnailUrl !== undefined ? { thumbnailUrl } : {}),
        },
      }),
    parse: (json) => parseMessageResult(json, 'Highlight URL updated'),
    fallback: 'Failed to update highlight URL',
    ErrorClass: AdminContentError,
  });
}

export type UpsertMatchHighlightUrlResult = {
  id: string;
  url: string;
};

export async function upsertMatchHighlightUrl(
  competitionId: string,
  url: string,
  thumbnailUrl?: string,
): Promise<UpsertMatchHighlightUrlResult> {
  return executeSdkEnvelopeRequest({
    request: async () => {
      const response = await fetchWithAuthRecovery(
        `${apiConfig.baseUrl}/v1/admin/content/matches/${encodeURIComponent(competitionId)}/highlight-url`,
        {
          method: 'PUT',
          headers: {
            ...buildApiHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            url,
            ...(thumbnailUrl !== undefined ? { thumbnailUrl } : {}),
          }),
        },
      );
      return { raw: response };
    },
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : {};
      return {
        id: asString(data['id']),
        url: asString(data['url']) || url,
      };
    },
    fallback: 'Failed to save highlight URL',
    ErrorClass: AdminContentError,
  });
}

export async function listOttMappingOptions(): Promise<AdminOttMappingOptions> {
  return executeSdkEnvelopeRequest({
    request: async () => {
      const response = await fetchWithAuthRecovery(
        `${apiConfig.baseUrl}/v1/admin/content/ott-mapping-options`,
        { headers: buildApiHeaders() },
      );
      return { raw: response };
    },
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : {};
      return {
        sports: parseCatalogOptions(data['sports']),
        leagues: parseLeagueOptions(data['leagues']),
        ottPlatforms: parseCatalogOptions(
          data['ottPlatforms'] ?? data['ott_platforms'],
        ),
      };
    },
    fallback: 'Failed to load OTT mapping options',
    ErrorClass: AdminContentError,
  });
}

function parseCatalogOptions(value: unknown): AdminOttMappingOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!isRecord(item) || typeof item.id !== 'string') return null;
      return {
        id: item.id,
        code: asString(item.code),
        name: asString(item.name),
      };
    })
    .filter((item): item is AdminOttMappingOption => item != null);
}

function parseLeagueOptions(value: unknown): AdminOttMappingLeagueOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!isRecord(item) || typeof item.id !== 'string') return null;
      return {
        id: item.id,
        sportId: asString(item.sportId),
        code: asString(item.code),
        name: asString(item.name),
      };
    })
    .filter((item): item is AdminOttMappingLeagueOption => item != null);
}

export async function listOttMappings(): Promise<AdminOttMapping[]> {
  return executeSdkEnvelopeRequest({
    request: () => adminContentApi.adminContentControllerListOttMappingsV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = Array.isArray(envelope['data'])
        ? envelope['data']
        : Array.isArray(json)
          ? json
          : [];
      return data
        .map(parseOttMapping)
        .filter((item): item is AdminOttMapping => item != null);
    },
    fallback: 'Failed to load OTT mappings',
    ErrorClass: AdminContentError,
  });
}

export async function createOttMapping(
  input: CreateOttMappingInput,
): Promise<AdminOttMapping | AdminMutationMessageResult> {
  const createOttMappingDto: CreateOttMappingInput = {
    sport_id: input.sport_id,
    league_id: input.league_id,
    ott_platform_id: input.ott_platform_id,
  };
  if (input.priority != null) createOttMappingDto.priority = input.priority;

  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerCreateOttMappingV1Raw({
        createOttMappingDto,
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const mapped = parseOttMapping(envelope.data);
      if (mapped) return mapped;
      return parseMessageResult(json, 'OTT mapping created');
    },
    fallback: 'Failed to create OTT mapping',
    ErrorClass: AdminContentError,
  });
}

export async function updateOttMapping(
  id: string,
  input: UpdateOttMappingInput,
): Promise<AdminMutationMessageResult> {
  const updateOttMappingDto: UpdateOttMappingInput = {};
  if (input.ott_platform_id)
    updateOttMappingDto.ott_platform_id = input.ott_platform_id;
  if (input.priority != null) updateOttMappingDto.priority = input.priority;
  if (input.listing_url !== undefined)
    updateOttMappingDto.listing_url = input.listing_url;

  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerUpdateOttMappingV1Raw({
        id,
        updateOttMappingDto,
      }),
    parse: (json) => parseMessageResult(json, 'OTT mapping updated'),
    fallback: 'Failed to update OTT mapping',
    ErrorClass: AdminContentError,
  });
}

export async function deleteOttMapping(
  id: string,
): Promise<AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerDeleteOttMappingV1Raw({ id }),
    parse: (json) => parseMessageResult(json, 'OTT mapping deleted'),
    fallback: 'Failed to delete OTT mapping',
    ErrorClass: AdminContentError,
  });
}

export async function listYouTubeChannels(): Promise<AdminYouTubeChannel[]> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerListYouTubeChannelsV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      if (!Array.isArray(envelope.data)) return [];
      return envelope.data
        .map(parseYouTubeChannel)
        .filter((item): item is AdminYouTubeChannel => item != null);
    },
    fallback: 'Failed to load YouTube channels',
    ErrorClass: AdminContentError,
  });
}

export async function createYouTubeChannel(
  input: CreateYouTubeChannelInput,
): Promise<AdminYouTubeChannel | AdminMutationMessageResult> {
  const createYouTubeChannelDto: CreateYouTubeChannelInput = {
    code: input.code,
    name: input.name,
    channel_id: input.channel_id,
    channel_url: input.channel_url,
  };

  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerCreateYouTubeChannelV1Raw({
        createYouTubeChannelDto,
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const mapped = parseYouTubeChannel(envelope.data);
      if (mapped) return mapped;
      return parseMessageResult(json, 'YouTube channel created');
    },
    fallback: 'Failed to create YouTube channel',
    ErrorClass: AdminContentError,
  });
}

export async function updateYouTubeChannel(
  id: string,
  input: UpdateYouTubeChannelInput,
): Promise<AdminMutationMessageResult> {
  const updateYouTubeChannelDto: UpdateYouTubeChannelInput = {};
  if (input.name) updateYouTubeChannelDto.name = input.name;
  if (input.channel_id) updateYouTubeChannelDto.channel_id = input.channel_id;
  if (input.channel_url !== undefined)
    updateYouTubeChannelDto.channel_url = input.channel_url;

  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerUpdateYouTubeChannelV1Raw({
        id,
        updateYouTubeChannelDto,
      }),
    parse: (json) => parseMessageResult(json, 'YouTube channel updated'),
    fallback: 'Failed to update YouTube channel',
    ErrorClass: AdminContentError,
  });
}

export async function deleteYouTubeChannel(
  id: string,
): Promise<AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerDeleteYouTubeChannelV1Raw({
        id,
      }),
    parse: (json) => parseMessageResult(json, 'YouTube channel deleted'),
    fallback: 'Failed to delete YouTube channel',
    ErrorClass: AdminContentError,
  });
}

export async function listLeagueChannelMappings(): Promise<
  AdminLeagueChannelMapping[]
> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerListLeagueChannelMappingsV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      if (!Array.isArray(envelope.data)) return [];
      return envelope.data
        .map(parseLeagueChannelMapping)
        .filter((item): item is AdminLeagueChannelMapping => item != null);
    },
    fallback: 'Failed to load league-channel mappings',
    ErrorClass: AdminContentError,
  });
}

export async function createLeagueChannelMapping(
  input: CreateLeagueChannelMappingInput,
): Promise<AdminLeagueChannelMapping | AdminMutationMessageResult> {
  const createLeagueChannelMappingDto: CreateLeagueChannelMappingInput = {
    league_id: input.league_id,
    youtube_channel_id: input.youtube_channel_id,
  };
  if (input.priority != null)
    createLeagueChannelMappingDto.priority = input.priority;

  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerCreateLeagueChannelMappingV1Raw({
        createLeagueChannelMappingDto,
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const mapped = parseLeagueChannelMapping(envelope.data);
      if (mapped) return mapped;
      return parseMessageResult(json, 'League-channel mapping created');
    },
    fallback: 'Failed to create league-channel mapping',
    ErrorClass: AdminContentError,
  });
}

export type UpdateLeagueChannelMappingInput = {
  youtube_channel_id?: string;
  priority?: number;
  reason: string;
};

export async function updateLeagueChannelMapping(
  id: string,
  input: UpdateLeagueChannelMappingInput,
): Promise<AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerUpdateLeagueChannelMappingV1Raw({
        id,
        updateLeagueChannelMappingDto: input,
      }),
    parse: (json) => parseMessageResult(json, 'League-channel mapping updated'),
    fallback: 'Failed to update league-channel mapping',
    ErrorClass: AdminContentError,
  });
}

export async function deleteLeagueChannelMapping(
  id: string,
  reason: string,
): Promise<AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerDeleteLeagueChannelMappingV1Raw({
        id,
        deleteLeagueChannelMappingDto: { reason },
      }),
    parse: (json) => parseMessageResult(json, 'League-channel mapping deleted'),
    fallback: 'Failed to delete league-channel mapping',
    ErrorClass: AdminContentError,
  });
}

export type AdminMappingHistoryEntry = {
  id: string;
  entityId: string;
  action: string;
  changedBy: string;
  createdAt: string;
};

export async function listMappingHistory(
  entityId?: string,
): Promise<AdminMappingHistoryEntry[]> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminContentApi.adminContentControllerListMappingHistoryV1Raw({
        ...(entityId ? { entityId } : {}),
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      if (!Array.isArray(envelope.data)) return [];
      return envelope.data
        .map((entry: unknown) => {
          if (!isRecord(entry) || typeof entry.id !== 'string') return null;
          return {
            id: entry.id,
            entityId: asString(entry.entityId),
            action: asString(entry.action),
            changedBy: asString(entry.changedBy),
            createdAt: asString(entry.createdAt),
          };
        })
        .filter((item): item is AdminMappingHistoryEntry => item != null);
    },
    fallback: 'Failed to load mapping history',
    ErrorClass: AdminContentError,
  });
}
